import { isTauri } from '@/tauri/utils';
import { hasLocalePathPrefix } from '@/utils/i18n/supportedLocales';

/**
 * Prefixes a path with the given locale if it doesn't already have one.
 * Handles absolute URLs by returning them unchanged.
 * Skips locale prefix on desktop (Tauri) since static export doesn't generate locale-prefixed routes.
 *
 * @param locale - The locale to prefix (e.g., 'en', 'fr-FR')
 * @param path - The path to potentially prefix
 * @returns The path with locale prefix, or unchanged if absolute URL, already prefixed, or desktop
 *
 * @example
 * prefixWithLocale('fr-FR', '/scribe') // => '/fr-FR/scribe' (web), '/scribe' (desktop)
 * prefixWithLocale('fr-FR', '/en/scribe') // => '/en/scribe' (already prefixed)
 * prefixWithLocale('fr-FR', 'https://example.com') // => 'https://example.com' (absolute)
 */
export function prefixWithLocale(locale: string, path: string): string {
  // Return absolute URLs unchanged (including protocol-relative URLs like //example.com)
  if (/^(https?:)?\/\//i.test(path)) {
    return path;
  }

  // Desktop uses static export — locale-prefixed routes don't exist
  if (isTauri()) {
    return path.startsWith('/') ? path : `/${path}`;
  }

  // Normalize path to ensure it starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Check if path already has a locale prefix (e.g., /fr-FR/scribe)
  const hasLocalePrefix = hasLocalePathPrefix(normalizedPath);

  return hasLocalePrefix ? normalizedPath : `/${locale}${normalizedPath}`;
}
